/**
 * Centralised Indian (en-IN) date formatting — the SINGLE source of truth for
 * every user-facing date in the app. Do not call `.toLocaleDateString()` /
 * `new Date(...).toLocaleString()` ad-hoc anywhere else; import from here (or use
 * the <DateText> component) so dates are consistent and always Indian-formatted.
 *
 * Canonical display format: "DD MMM YYYY" (e.g. 04 May 2026) using the en-IN locale.
 * Input/payload values (yyyy-mm-dd) are handled by formatDateForInput / localToday,
 * which must NOT be changed to a display format.
 */

const LOCALE = "en-IN";

/** Parse to a valid Date or return null. */
const toDate = (value: string | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/** "04 May 2026" */
export const formatDate = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString(LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** "04 May 2026, 02:30 PM" */
export const formatDateTime = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleString(LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/** "02:30 PM" */
export const formatTime = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleTimeString(LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/** "Thursday, 04 May 2026" */
export const formatDateLong = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString(LOCALE, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

/** "May 2026" — for calendar / month headers */
export const formatMonthYear = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString(LOCALE, {
    month: "long",
    year: "numeric",
  });
};

/** "May" — short month name */
export const formatMonthShort = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString(LOCALE, { month: "short" });
};

/** "Thu" — short weekday name */
export const formatWeekdayShort = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString(LOCALE, { weekday: "short" });
};

/** "04/05/2026" — pure numeric Indian (DD/MM/YYYY) when a compact form is needed */
export const formatDateNumeric = (value: string | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString(LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/** Today as YYYY-MM-DD in LOCAL timezone (for <input type="date"> / API payloads). */
export const localToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Any date string -> YYYY-MM-DD in LOCAL timezone (for <input type="date"> values). */
export const formatDateForInput = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = toDate(dateStr);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
